"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { HolesLayer } from "./overlayKit/HolesLayer";
import { DevaKathaHypeCta, ctaBeatsFromWords } from "./DevaKathaHypeCta";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Badrinath — Badri aur Nath (5dc90643) — Trimbakeshwar holes treatment.
// MiniMax-H3 lane 1920x1088 → assemble 1920x1080 @24 → NVIDIA 4K (24 fps). Comp 1280x720 @ 24 fps, scale=3 → 3840x2160.
// Every `start` RESOLVED from word_timestamps_hindi (4144 words, ends 1502.60s vs
// duration 1502.60s, not drifted). Generated by gen_badrinath_overlays.py. 47 cards = 1 per 32.0s.
// Brief (house, since Khade Hanuman): short and crisp, <=4 tokens per card.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";

const BASE_FS = 40, BIG_FS = 56, SMALL_FS = 28;   // Yash 2026-08-04 LAW: 40/56/28.
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -8;

const CTA_TITLE = "बद्रीनाथ: बद्री कौन, और नाथ कौन?";
const CTA_HANDLE = "@DevaKatha";
const CTA_COMMENT_TEXT = "जय बद्री विशाल 🙏 इस साल कपाट बंद होने से पहले दर्शन हुए, ये कहानी सुनकर आँखें भर आईं।";
// ⛔ https R2, never staticFile() — factory_4k_full pushes only src/, public/ is frozen in the image.
const CTA_SEG = (n: number) =>
  `https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/overlay_assets/badrinath_cta${n}_seg.mp4`;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

export const BADRINATH_PHRASES: Phrase[] = [
  { start: 7.79, out: 11.19, lines: [[{ t: "एक", c: "w", small: true }, { t: "बद्री,", c: "y", big: true }], [{ t: "एक", c: "w", small: true }, { t: "नाथ", c: "y", big: true }]] },   // बद्री,
  { start: 27.41, out: 30.81, lines: [[{ t: "पहाड़", c: "w", small: true }, { t: "पार:", c: "w" }], [{ t: "केदारनाथ", c: "y", big: true }]] },   // केदारनाथ
  { start: 76.51, out: 79.91, lines: [[{ t: "वो", c: "w", small: true }, { t: "बालक:", c: "w" }], [{ t: "भगवान", c: "y", big: true }, { t: "विष्णु", c: "y", big: true }]] },   // विष्णु
  { start: 112.08, out: 116.08, lines: [[{ t: "बोलंदा", c: "y", big: true }, { t: "बद्रीनाथ", c: "y", big: true }]] },   // बोलंदा
  { start: 140.85, out: 144.85, lines: [[{ t: "कलाई", c: "r", big: true }, { t: "टूटेगी,", c: "r", big: true }], [{ t: "घर", c: "y", big: true }, { t: "ख़ाली", c: "y", big: true }]] },   // टूटेगी
  { start: 159.52, out: 162.92, lines: [[{ t: "कोई", c: "w", small: true }, { t: "महल", c: "y", big: true }, { t: "नहीं", c: "r", big: true }]] },   // महल
  { start: 188.75, out: 192.15, lines: [[{ t: "बेर", c: "y", big: true }, { t: "का", c: "w", small: true }, { t: "पेड़:", c: "y" }], [{ t: "बदरी", c: "r", big: true }]] },   // बदरी
  { start: 223.27, out: 226.67, lines: [[{ t: "बद्री:", c: "y", big: true }, { t: "लक्ष्मी", c: "y", big: true }]] },   // बद्री,
  { start: 227.35, out: 230.75, lines: [[{ t: "नाथ:", c: "y", big: true }, { t: "उनके", c: "w", small: true }, { t: "स्वामी", c: "y", big: true }]] },   // नाथ,
  { start: 238.80, out: 242.20, lines: [[{ t: "बद्री", c: "y", big: true }, { t: "पहले,", c: "y", big: true }], [{ t: "नाथ", c: "r", big: true }, { t: "बाद", c: "r", big: true }]] },   // बद्री
  { start: 295.48, out: 298.88, lines: [[{ t: "नर", c: "y", big: true }, { t: "और", c: "w", small: true }, { t: "नारायण", c: "y", big: true }]] },   // नर
  { start: 358.39, out: 361.79, lines: [[{ t: "ऊरु", c: "y", big: true }, { t: "से:", c: "w", small: true }, { t: "उर्वशी", c: "r", big: true }]] },   // उर्वशी
  { start: 385.52, out: 388.92, lines: [[{ t: "नर:", c: "y", big: true }, { t: "अर्जुन", c: "r", big: true }], [{ t: "नारायण:", c: "y", big: true }, { t: "श्रीकृष्ण", c: "r", big: true }]] },   // अर्जुन
  { start: 441.92, out: 445.32, lines: [[{ t: "बद्री:", c: "y", big: true }, { t: "नारायण", c: "y", big: true }], [{ t: "केदार:", c: "r", big: true }, { t: "महादेव", c: "r", big: true }]] },   // नारायण,
  { start: 458.26, out: 461.66, lines: [[{ t: "1.", c: "w", small: true }, { t: "आदि", c: "y", big: true }, { t: "केदारेश्वर", c: "y", big: true }]] },   // केदारेश्वर
  { start: 474.65, out: 478.05, lines: [[{ t: "2.", c: "w", small: true }, { t: "ब्रह्म", c: "y", big: true }, { t: "कपाल", c: "y", big: true }]] },   // कपाल
  { start: 500.42, out: 503.82, lines: [[{ t: "3.", c: "w", small: true }, { t: "रक्षक", c: "y" }, { t: "घंटाकर्ण", c: "y", big: true }]] },   // घंटाकर्ण
  { start: 541.64, out: 545.04, lines: [[{ t: "आख़िरी", c: "w", small: true }, { t: "आदेश:", c: "w" }], [{ t: "उद्धव", c: "y", big: true }, { t: "जी", c: "y" }]] },   // उद्धव
  { start: 593.11, out: 596.51, lines: [[{ t: "माणा,", c: "y", big: true }], [{ t: "सरस्वती", c: "w" }, { t: "के", c: "w", small: true }, { t: "किनारे", c: "w" }]] },   // माणा,
  { start: 608.53, out: 611.93, lines: [[{ t: "भीम", c: "y", big: true }, { t: "पुल", c: "y", big: true }], [{ t: "द्रौपदी", c: "r", big: true }, { t: "के लिए", c: "w", small: true }]] },   // भीम
  { start: 650.21, out: 653.61, lines: [[{ t: "आख़िरी", c: "r", big: true }, { t: "गाँव", c: "y", big: true }]] },   // आख़िरी
  { start: 716.07, out: 720.57, lines: [[{ t: "नारद", c: "y", big: true }, { t: "शिला", c: "y", big: true }], [{ t: "नारद", c: "r", big: true }, { t: "कुंड", c: "r", big: true }]] },   // शिला
  { start: 743.88, out: 747.88, lines: [[{ t: "आदि", c: "y", big: true }, { t: "शंकराचार्य", c: "y", big: true }]] },   // केरल
  { start: 759.31, out: 762.71, lines: [[{ t: "बर्फ़ीले", c: "y" }, { t: "पानी", c: "y", big: true }], [{ t: "उतर", c: "r", big: true }]] },   // उतर
  { start: 796.49, out: 799.89, lines: [[{ t: "54", c: "r", big: true }, { t: "डिग्री", c: "r", big: true }]] },   // चौवन
  { start: 844.61, out: 848.01, lines: [[{ t: "पाँच", c: "y", big: true }, { t: "चट्टानें:", c: "w" }], [{ t: "पंचशिला", c: "y", big: true }]] },   // पंचशिला
  { start: 883.30, out: 886.70, lines: [[{ t: "रावल:", c: "y", big: true }], [{ t: "केरल", c: "r", big: true }, { t: "के", c: "w", small: true }, { t: "नंबूदरी", c: "r", big: true }]] },   // नंबूदरी
  { start: 962.40, out: 965.80, lines: [[{ t: "1803:", c: "r", big: true }, { t: "भूकंप", c: "r", big: true }]] },   // भूकंप
  { start: 989.14, out: 992.54, lines: [[{ t: "15", c: "y", big: true }, { t: "मीटर,", c: "y", big: true }], [{ t: "सोने", c: "r", big: true }, { t: "का गुंबद", c: "r", big: true }]] },   // पंद्रह
  { start: 1055.96, out: 1059.36, lines: [[{ t: "तलवारों", c: "w", small: true }, { t: "नहीं,", c: "w" }], [{ t: "हिसाब", c: "r", big: true }, { t: "की लड़ाई", c: "r", big: true }]] },   // हिसाब
  { start: 1083.18, out: 1087.18, lines: [[{ t: "1939:", c: "w", small: true }, { t: "मंदिर", c: "y" }, { t: "अधिनियम", c: "y", big: true }]] },   // अधिनियम
  { start: 1123.77, out: 1127.77, lines: [[{ t: "6", c: "r", big: true }, { t: "महीने", c: "r", big: true }, { t: "बंद", c: "r", big: true }]] },   // नवंबर
  { start: 1148.98, out: 1152.38, lines: [[{ t: "स्त्री", c: "y", big: true }, { t: "वेश,", c: "y" }], [{ t: "लक्ष्मी", c: "r", big: true }, { t: "की सखी", c: "r", big: true }]] },   // वेश
  { start: 1193.09, out: 1196.49, lines: [[{ t: "घी", c: "y", big: true }, { t: "में भिगोया:", c: "w" }], [{ t: "घृत", c: "r", big: true }, { t: "कंबल", c: "r", big: true }]] },   // घृत
  { start: 1234.16, out: 1237.56, lines: [[{ t: "2023:", c: "w", small: true }], [{ t: "भविष्यवाणी", c: "r", big: true }, { t: "ताज़ा", c: "r", big: true }]] },   // भविष्यवाणी
  { start: 1260.05, out: 1263.45, lines: [[{ t: "2", c: "y", big: true }, { t: "जनवरी", c: "y", big: true }, { t: "2023,", c: "y" }], [{ t: "रात", c: "r", big: true }]] },   // जनवरी
  { start: 1283.99, out: 1287.39, lines: [[{ t: "रातोंरात", c: "w", small: true }, { t: "दरारें", c: "r", big: true }]] },   // दरारें
  { start: 1306.62, out: 1311.12, lines: [[{ t: "12", c: "y", big: true }, { t: "दिन:", c: "y" }], [{ t: "5.4", c: "r", big: true }, { t: "सेंटीमीटर", c: "r", big: true }]] },   // बारह
  { start: 1334.90, out: 1338.30, lines: [[{ t: "बेहिसाब", c: "w", small: true }, { t: "निर्माण,", c: "w" }], [{ t: "रिषिगंगा", c: "r", big: true }, { t: "2021", c: "r", big: true }]] },   // रिषिगंगा
  { start: 1356.08, out: 1359.48, lines: [[{ t: "नरसिंह:", c: "y", big: true }], [{ t: "क्रोध", c: "w", small: true }, { t: "नहीं,", c: "w" }, { t: "शांत", c: "r", big: true }]] },   // शांत
  { start: 1364.06, out: 1367.46, lines: [[{ t: "साल दर साल", c: "w", small: true }], [{ t: "कलाई", c: "r", big: true }, { t: "पतली", c: "r", big: true }]] },   // पतली
  { start: 1374.83, out: 1378.83, lines: [[{ t: "जय", c: "r", big: true }, { t: "और विजय", c: "r", big: true }], [{ t: "मिल", c: "y", big: true }, { t: "जाएँगे", c: "y", big: true }]] },   // जय
  { start: 1389.69, out: 1393.09, lines: [[{ t: "घर:", c: "w", small: true }], [{ t: "भविष्य", c: "y", big: true }, { t: "बद्री", c: "y", big: true }]] },   // भविष्य
  { start: 1435.98, out: 1439.38, lines: [[{ t: "बद्री", c: "y", big: true }, { t: "पहले,", c: "y", big: true }], [{ t: "नाथ", c: "r", big: true }, { t: "बाद", c: "r", big: true }]] },   // बद्री
  { start: 1467.51, out: 1470.91, lines: [[{ t: "बद्री", c: "y", big: true }, { t: "विशाल", c: "y", big: true }, { t: "की", c: "w", small: true }, { t: "जय", c: "r", big: true }]] },   // जय
  { start: 1473.64, out: 1478.14, lines: [[{ t: "छिपने", c: "y", big: true }, { t: "का रूप:", c: "y" }], [{ t: "पाँच", c: "r", big: true }, { t: "हिस्से", c: "r", big: true }]] },   // छिपने
  { start: 1483.72, out: 1487.72, lines: [[{ t: "अगली", c: "w", small: true }, { t: "वीडियो:", c: "w" }], [{ t: "केदारनाथ", c: "y", big: true }]] },   // केदारनाथ
];

const PhraseCard: React.FC<{ p: Phrase }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  let k = 0;
  return (
    <AbsoluteFill style={{ padding: PAD, alignItems: "flex-start", justifyContent: "flex-start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: p.lineGap ?? LINE_GAP }}>
        {p.lines.map((line, li) => (
          <div key={li} style={{ display: "flex", alignItems: "flex-end", gap: 16, lineHeight: p.lh ?? LH }}>
            {line.map((w, wi) => {
              const delay = k++ * STAGGER;
              const prog = interpolate(t, [delay, delay + DUR], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              const fs = w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS;
              return (
                <span key={wi} style={{
                  fontFamily, fontWeight: w.big ? 900 : 800, fontSize: fs, color: col(w.c),
                  textShadow: SH, letterSpacing: p.ls,
                  transform: `translateY(${(1 - prog) * -DROP}px)`, opacity: prog,
                }}>{w.t}</span>
              );
            })}
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

const Teaser: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const dots = ["", ".", "..", "..."][Math.floor((frame / fps * 1.5) % 4)];
  return (
    <AbsoluteFill style={{ padding: PAD, alignItems: "flex-start", justifyContent: "flex-end" }}>
      <span style={{ fontFamily, fontWeight: 800, fontSize: SMALL_FS, color: YELLOW, textShadow: SH }}>
        आगे देखिए{dots}
      </span>
    </AbsoluteFill>
  );
};

const WatchTip: React.FC = () => (
  <AbsoluteFill style={{ padding: PAD, alignItems: "flex-end", justifyContent: "flex-end" }}>
    <div style={{ fontFamily, fontWeight: 800, fontSize: SMALL_FS, color: YELLOW, textShadow: SH }}>Watch at 1.2x</div>
    <div style={{ fontFamily, fontWeight: 700, fontSize: SMALL_FS * 0.7, color: WHITE, textShadow: SH }}>for best experience</div>
  </AbsoluteFill>
);

// 🔥 The <Sequence from={S(...)} /> durationInFrames={S(...)} values below MUST stay
// NUMERIC LITERALS — build_overlay_windows.py AND cta_segs.py both regex-scrape them.
export const BadrinathOverlays: React.FC = () => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      <HolesLayer holes={HOLES_DATA_URI} width={1280} height={720}>
        {BADRINATH_PHRASES.map((p, i) => (
          <Sequence key={i} from={S(p.start)} durationInFrames={S(p.out - p.start)}>
            <PhraseCard p={p} />
          </Sequence>
        ))}
      </HolesLayer>

      <Sequence from={S(3)} durationInFrames={S(147)}>
        <Teaser />
      </Sequence>
      <Sequence from={S(0)} durationInFrames={S(5)}>
        <WatchTip />
      </Sequence>

      {/* CTA1 — लाइक 279.14 · हाइप 279.59 (कमेंट 282.20 has no tap) */}
      <Sequence from={S(277.48)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(1)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ like: 279.14, hype: 279.59 }, 277.48)} />
      </Sequence>
      {/* CTA2 — "सब्सक्राइब का बटन दबाकर" सब्सक्राइब 1250.20 (sub-only cluster) */}
      <Sequence from={S(1248.54)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(2)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ sub: 1250.2 }, 1248.54)} />
      </Sequence>
      {/* CTA3 — outro: सब्सक्राइब 1492.92 + share ask भेजिए 1495.64 in ONE cluster. Window 1491.26→1502.86;
          like/hype fill in after the share weld (badge 9.44, fall 11.54 → dur 11.60). Narration ends 1502.60, the assembled base
          carries a 7 s freeze tail to ~1509.6, so the phone and its 12.0 s seg cut both land inside the base. */}
      <Sequence from={S(1491.26)} durationInFrames={S(11.6)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(3)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ sub: 1492.92, share: 1495.64 }, 1491.26)} />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
